import React from 'react';
import { X, User, Mail, Phone, Globe, Building2, Tags, MessageSquare, Briefcase, CalendarClock, Edit3, Copy, ExternalLink } from 'lucide-react';

export type FullClient = {
  id: string;
  name: string;
  platform: string;
  // Identité & entreprise
  company_name?: string | null;
  client_type?: string | null;

  // Contact
  email_primary?: string | null;
  email_secondary?: string | null;
  phone_primary?: string | null;
  phone_whatsapp?: string | null;
  preferred_contact_method?: string | null;

  // Localisation
  timezone?: string | null;
  preferred_language?: string | null;
  country?: string | null;
  city?: string | null;

  // Business
  industry?: string | null;
  services_needed?: string[] | null;
  budget_range?: string | null;
  collaboration_frequency?: string | null;

  // Commercial
  acquisition_source?: string | null;
  client_status?: string | null;
  priority_level?: string | null;
  payment_terms?: string | null;

  // Notes & suivi
  availability_notes?: string | null;
  important_notes?: string | null;
  next_action?: string | null;
  next_action_date?: string | null;

  // Divers
  tags?: string[] | null;
  created_at?: string | null;
};

interface ClientViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  client: FullClient | null;
  loading?: boolean;
  onEdit?: (client: FullClient) => void;
}

const Row: React.FC<{ label: string; value?: React.ReactNode; copyable?: boolean }> = ({ label, value, copyable = false }) => {
  const handleCopy = () => {
    if (typeof value === 'string') {
      navigator.clipboard.writeText(value);
    }
  };

  return (
    <div className="group flex flex-col sm:flex-row sm:items-center sm:justify-between py-3 px-4 hover:bg-[rgb(var(--subtle))] rounded-lg transition-all duration-200">
      <div className="text-sm font-medium text-[rgb(var(--muted))] mb-1 sm:mb-0">{label}</div>
      <div className="flex items-center gap-2">
        <div className="text-sm text-[rgb(var(--fg))] break-words sm:ml-6">
          {value ?? <span className="text-[rgb(var(--muted-2))] italic">Not specified</span>}
        </div>
        {copyable && typeof value === 'string' && value !== 'Not specified' && (
          <button
            onClick={handleCopy}
            className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 p-1 hover:bg-[rgb(var(--subtle))] rounded"
            title="Copy"
          >
            <Copy className="w-3 h-3 text-[rgb(var(--muted))] hover:text-[rgb(var(--fg))]" />
          </button>
        )}
      </div>
    </div>
  );
};

const Section: React.FC<{ icon: React.ReactNode; title: string; children: React.ReactNode; gradient?: boolean }> = ({ icon, title, children, gradient = false }) => (
  <div className={`
    ${gradient ? 'bg-[rgb(var(--subtle))] border-[rgb(var(--border-strong))]' : 'bg-[rgb(var(--card))] border-[rgb(var(--border))]'} 
    border rounded-xl p-5 shadow-[var(--shadow-soft)] hover:shadow-[var(--shadow-md)] transition-all duration-300 hover:scale-[1.02]
  `}>
    <div className="flex items-center gap-3 mb-4">
      <div className="p-2 rounded-lg bg-[rgb(var(--subtle))]">
        {icon}
      </div>
      <h3 className="text-lg font-semibold text-[rgb(var(--fg))]">{title}</h3>
    </div>
    <div className="space-y-1">{children}</div>
  </div>
);

const ClientViewModal: React.FC<ClientViewModalProps> = ({ isOpen, onClose, client, loading, onEdit }) => {
  if (!isOpen) return null;

  const TagList = (client?.tags ?? []).length ? (
    <div className="flex flex-wrap gap-2">
      {(client?.tags ?? []).map((t, i) => (
        <span
          key={i}
          className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-full bg-[rgb(var(--card))] text-[rgb(var(--primary))] border border-[rgb(var(--border-strong))] hover:bg-[rgb(var(--subtle))] transition-all duration-200"
        >
          {t}
        </span>
      ))}
    </div>
  ) : (
    <span className="text-[rgb(var(--muted-2))] italic">No tags</span>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      {/* Panel */}
      <div className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-2xl bg-[rgb(var(--elevated))] border border-[rgb(var(--border))] shadow-[var(--shadow-md)]">
        <div className="sticky top-0 z-10 flex items-center justify-between px-8 py-6 border-b border-[rgb(var(--border))] bg-[rgb(var(--elevated))]">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-[rgb(var(--primary))]">
              <User className="w-6 h-6 text-[rgb(var(--primary-contrast))]" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-[rgb(var(--fg))]">
                {client?.name || 'Client'}
              </h2>
              {client?.created_at && (
                <p className="text-sm text-[rgb(var(--muted))]">
                  Created on {new Date(client.created_at).toLocaleDateString('en-US')}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {onEdit && client && (
              <button
                onClick={() => onEdit(client)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[rgb(var(--primary))] text-[rgb(var(--primary-contrast))] text-sm font-medium hover:opacity-90 focus:ring-2 focus:ring-[rgb(var(--primary))]/50 shadow-[var(--shadow-soft)] hover:shadow-[var(--shadow-md)] transition-all duration-200"
              >
                <Edit3 className="w-4 h-4" />
                Edit
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-[rgb(var(--subtle))] text-[rgb(var(--muted))] hover:text-[rgb(var(--fg))] transition-all duration-200"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="p-10 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#9c68f2]" />
            <span className="ml-3 text-[rgb(var(--muted))]">Loading…</span>
          </div>
        ) : (
          <div className="p-8 space-y-6">
            <Section icon={<User className="w-5 h-5 text-[#9c68f2]" />} title="Identity" gradient>
              <Row label="Full name" value={client?.name} copyable />
              <Row label="Client type" value={client?.client_type} />
              <Row label="Company" value={client?.company_name} />
              <Row label="Platform" value={client?.platform} />
            </Section>

            <Section icon={<Mail className="w-5 h-5 text-[#9c68f2]" />} title="Contact">
              <Row label="Primary email" value={client?.email_primary} copyable />
              <Row label="Secondary email" value={client?.email_secondary} copyable />
              <Row label="Phone" value={client?.phone_primary} copyable />
              <Row label="WhatsApp" value={client?.phone_whatsapp} copyable />
              <Row label="Preferred contact method" value={client?.preferred_contact_method} />
            </Section>

            <Section icon={<Globe className="w-5 h-5 text-[#9c68f2]" />} title="Location">
              <Row label="Country" value={client?.country} />
              <Row label="City" value={client?.city} />
              <Row label="Timezone" value={client?.timezone} />
              <Row label="Preferred language" value={client?.preferred_language} />
            </Section>

            <Section icon={<Briefcase className="w-5 h-5 text-[#9c68f2]" />} title="Business" gradient>
              <Row label="Industry" value={client?.industry} />
              <Row
                label="Services needed"
                value={
                  (client?.services_needed?.length
                    ? (client?.services_needed || []).join(', ')
                    : null)
                }
              />
              <Row label="Estimated budget" value={client?.budget_range} />
              <Row label="Collaboration frequency" value={client?.collaboration_frequency} />
            </Section>

            <Section icon={<Building2 className="w-5 h-5 text-[#9c68f2]" />} title="Commercial">
              <Row label="Acquisition source" value={client?.acquisition_source} />
              <Row label="Client status" value={client?.client_status} />
              <Row label="Priority level" value={client?.priority_level} />
              <Row label="Payment terms" value={client?.payment_terms} />
            </Section>

            <Section icon={<MessageSquare className="w-5 h-5 text-[#9c68f2]" />} title="Notes & Observations">
              <Row label="Availability" value={client?.availability_notes} />
              <Row label="Important notes" value={client?.important_notes} />
            </Section>

            <Section icon={<CalendarClock className="w-5 h-5 text-[#9c68f2]" />} title="Follow-up & Actions" gradient>
              <Row label="Next action" value={client?.next_action} />
              <Row
                label="Next action date"
                value={
                  client?.next_action_date
                    ? new Date(client.next_action_date).toLocaleDateString('en-US')
                    : null
                }
              />
            </Section>

            <Section icon={<Tags className="w-5 h-5 text-[#9c68f2]" />} title="Tags & Categories">
              <div className="py-2">{TagList}</div>
            </Section>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientViewModal;
